import { DISPLAY_NAME_MAX, normalizeDisplayName } from "../../api/src/shared/display-name.js";
import { updateDisplayName, type User } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { ApiError } from "../lib/http.js";
import { NAME_PROMPT } from "./nameGate.js";
import { actionRow, type Popup, type PopupSpec } from "./ui.js";

/**
 * The account's own name (g73), asked by Settings and before an owner's first
 * share link. The rules and most of the words are g72's for a visitor; what
 * an owner is told differs, and says nothing about what came before: saving
 * a name renames nobody else and merges nothing.
 */
export const ACCOUNT_NAME = {
  label: NAME_PROMPT.label,
  addTitle: "Add name",
  changeTitle: "Change name",
  none: "No name yet",
  settingsExplain: "People you share a list with see this name next to what you add.",
  linkTitle: NAME_PROMPT.title,
  linkExplain: "People with the link will see this name next to what you add. You can change it later in Settings.",
  save: "Save",
  continue: NAME_PROMPT.save,
  cancel: NAME_PROMPT.cancel,
  empty: NAME_PROMPT.empty,
  tooLong: NAME_PROMPT.tooLong,
  invalid: NAME_PROMPT.invalid,
  limited: NAME_PROMPT.limited,
  offline: "Can't reach Trayflux. Saving your name needs a connection.",
  saved: "Name saved.",
} as const;

export interface AccountNameForm {
  node: HTMLFormElement;
  field: HTMLInputElement;
  /** A save is on its way: every way out but its answer waits. */
  busy(): boolean;
}

export interface AccountNameOptions {
  /** The field's id, unique on the page. */
  id: string;
  initial: string;
  explain: string;
  submit: string;
  announce(message: string): void;
  /** After the server has the name. The form is still up. */
  saved(user: User): void;
  cancel(): void;
}

/**
 * One field and its two buttons. An empty name is always refused, so a name
 * once set can be changed and never cleared: the first link asks for one, and
 * nothing afterwards takes it away.
 */
export function accountNameForm(options: AccountNameOptions): AccountNameForm {
  const explainId = `${options.id}-explain`;
  const errorId = `${options.id}-error`;
  const field = el("input", {
    attrs: {
      id: options.id,
      type: "text",
      autocomplete: "name",
      maxlength: DISPLAY_NAME_MAX * 2,
      "aria-describedby": `${explainId} ${errorId}`,
    },
  });
  field.value = options.initial;
  const error = el("p", { class: "field-error", attrs: { id: errorId, hidden: true } });
  const submit = el("button", { text: options.submit, attrs: { type: "submit" } });
  const cancel = el("button", {
    class: "secondary",
    text: ACCOUNT_NAME.cancel,
    attrs: { type: "button" },
    on: { click: () => { if (!busy) options.cancel(); } },
  });
  let busy = false;

  const say = (message: string | undefined) => {
    error.textContent = message ?? "";
    error.hidden = message === undefined;
    if (message === undefined) field.removeAttribute("aria-invalid");
    else {
      field.setAttribute("aria-invalid", "true");
      options.announce(message);
    }
  };

  async function save(): Promise<void> {
    if (busy) return;
    const verdict = normalizeDisplayName(field.value);
    if (!verdict.ok) { say(verdict.code === "too_long" ? ACCOUNT_NAME.tooLong : ACCOUNT_NAME.invalid); field.focus(); return; }
    if (verdict.name === null) { say(ACCOUNT_NAME.empty); field.focus(); return; }
    busy = true;
    submit.disabled = true;
    cancel.disabled = true;
    node.setAttribute("aria-busy", "true");
    say(undefined);
    let user: User;
    try {
      user = await updateDisplayName(verdict.name);
    } catch (failure: unknown) {
      busy = false;
      submit.disabled = false;
      cancel.disabled = false;
      node.removeAttribute("aria-busy");
      // Status 0 is a request that never reached the server.
      say(failure instanceof ApiError && failure.status !== 0
        ? failure.status === 429 ? ACCOUNT_NAME.limited : failure.message
        : ACCOUNT_NAME.offline);
      field.focus();
      return;
    }
    busy = false;
    submit.disabled = false;
    cancel.disabled = false;
    node.removeAttribute("aria-busy");
    options.saved(user);
  }

  const node = el("form", {
    class: "account-name item-form",
    attrs: { novalidate: true },
    children: [
      el("p", { class: "muted", attrs: { id: explainId }, text: options.explain }),
      el("div", { class: "field", children: [el("label", { class: "field-label", text: ACCOUNT_NAME.label, attrs: { for: options.id } }), field, error] }),
      actionRow([cancel], submit),
    ],
    on: { submit: (event) => { event.preventDefault(); void save(); } },
  });
  field.addEventListener("input", () => { if (!error.hidden) say(undefined); });
  return { node, field, busy: () => busy };
}

interface Host {
  openPopup(spec: PopupSpec): Popup;
  announce(message: string): void;
  updateUser(user: User): void;
}

/** Settings' Add name or Change name. */
export function openAccountName(host: Host, user: User, returnFocusTo: HTMLElement | (() => HTMLElement | undefined)): Popup {
  const initial = typeof user.name === "string" ? user.name : "";
  const form = accountNameForm({
    id: "account-name",
    initial,
    explain: ACCOUNT_NAME.settingsExplain,
    submit: ACCOUNT_NAME.save,
    announce: host.announce,
    saved: (saved) => {
      host.updateUser(saved);
      popup.close();
      host.announce(ACCOUNT_NAME.saved);
    },
    cancel: () => popup.dismiss(),
  });
  const popup = host.openPopup({
    title: initial ? ACCOUNT_NAME.changeTitle : ACCOUNT_NAME.addTitle,
    body: form.node,
    focus: form.field,
    busy: form.busy,
    returnFocusTo,
    guard: {
      dirty: () => form.field.value.trim() !== initial,
      title: "Discard this name?",
      keep: "Keep editing",
      discard: "Discard",
    },
  });
  return popup;
}
